<script setup lang="ts">
import { type PropType, computed } from '@mimir/web/compat/vueCompat';
import { getCurrentInstance } from 'vue';
import { VBtn, VIcon } from 'vuetify/lib';

const props = defineProps({
  icon: {
    type: String,
    default: ''
  },
  defaultSlotIcon: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: ''
  },
  size: {
    type: String as PropType<'small' | 'default'>,
    default: 'default',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String as PropType<'flat' | 'text' | 'elevated' | 'tonal' | 'outlined' | 'plain'>,
    default: 'elevated',
  },
});

const vm = getCurrentInstance()!.proxy as Vue;
const mimirIcon = computed(() => vm.$slots['default']?.[0]?.tag?.includes('MimirIcon'));

const vuetify2Props = computed(() => {
  const baseProps = {
    small: props.size === 'small',
    icon: !!props.icon || mimirIcon.value || props.defaultSlotIcon,
    disabled: props.disabled,
  };

  switch (props.variant) {
    case 'flat':
      return { ...baseProps, flat: true };
    case 'text':
      return { ...baseProps, text: true };
    case 'outlined':
      return { ...baseProps, outlined: true };
    case 'plain':
      return { ...baseProps, plain: true };
    case 'tonal':
      return { ...baseProps, elevation: 1 };
    case 'elevated':
    default:
      return baseProps;
  }
});

</script>


<template>
  <VBtn
    :text="!icon && !mimirIcon && !defaultSlotIcon"
    v-bind="{ ...vuetify2Props, ...$attrs }"
    v-on="$listeners"
  >
    <VIcon
      v-if="icon"
      :small="size === 'small'"
      :color="color"
      v-text="icon"
    />
    <slot />
  </VBtn>
</template>

<style scoped lang="stylus">
.v-btn--text
  :deep(.v-btn__content)
    letter-spacing: 0
.v-btn--disabled :deep(.mimir-icon)
  opacity 0.3 !important
</style>
